<template>
  <div class="relative">
    <CDropdown
      head-class="cursor-pointer"
      :body-class="[
        'max-h-[360px] overflow-y-scroll',
        { 'pb-5': notifications?.length >= 5 },
      ]"
      wrapper-class="!-right-[74px] md:!right-[-110px]"
    >
      <template #head>
        <NotificationButton :have-notifications="unreadNotifications" />
      </template>
      <div class="relative">
        <div class="max-w-[338px]">
          <template v-if="notifications?.length">
            <NotificationItem
              v-for="(notification, index) in notifications"
              :key="index"
              v-bind="{ notification }"
              @click="showNotificationDetail(notification?.id)"
            />
          </template>
          <template v-else>
            <CNodata
              class="min-w-[338px]"
              image="/images/svg/no-data/46.svg"
              :title="$t('no_notifications_title')"
              :subtitle="$t('no_notifications_desc')"
            />
          </template>
          <div ref="more" />
          <Transition name="fade" mode="out-in">
            <div v-if="loadingMore">
              <div class="w-full flex-y-center justify-center mt-4">
                <div class="dots-loading" />
              </div>
            </div>
          </Transition>
        </div>
      </div>
      <template #actions>
        <div class="p-4" v-if="notifications?.length">
          <RouterLink to="/notifications">
            <CButton variant="dark" :text="$t('view_all_notifications')" />
          </RouterLink>
        </div>
      </template>
    </CDropdown>
  </div>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from "@vueuse/core";
import NotificationButton from "@/modules/Notification/components/NotificationButton.vue";
import CDropdown from "@/components/Common/Dropdown/CDropdown.vue";
import { useNotificationStore } from "@/modules/Notification/stores";
import { computed, onMounted, ref } from "vue";
import NotificationItem from "@/modules/Notification/components/NotificationItem.vue";
import CButton from "@/components/Common/CButton.vue";
import CNodata from "@/components/Common/CNodata.vue";

const notificationStore = useNotificationStore();

const more = ref(null);
const loadingMore = ref(false);

const notifications = computed(() => notificationStore.headerNotifications);
const pagesCount = computed(() => notificationStore.headerNotificationsCount);
const unreadNotifications = computed(
  () => notificationStore.unreadNotifications,
);

const loadMoreNotifications = () => {
  notificationStore.size.header += 5;
  notificationStore
    .getHeaderNotifications({ size: notificationStore.size.header })
    .finally(() => {
      loadingMore.value = false;
    });
};

const showNotificationDetail = (id: number) => {
  notificationStore.getNotificationsSingle(id, "header");
};

onMounted(() => {
  useIntersectionObserver(more, ([{ isIntersecting }]) => {
    if (notificationStore.size.header < pagesCount.value && isIntersecting) {
      loadingMore.value = isIntersecting;
      setTimeout(() => {
        loadMoreNotifications();
      }, 500);
    }
  });
});
</script>

<style scoped>
.dots-loading {
  width: 40px;
  height: 19.2px;
  background:
    radial-gradient(circle closest-side, #16cc53 90%, #0000) 0% 50%,
    radial-gradient(circle closest-side, #16cc53 90%, #0000) 50% 50%,
    radial-gradient(circle closest-side, #16cc53 90%, #0000) 100% 50%;
  background-size: calc(100% / 3) 9.6px;
  background-repeat: no-repeat;
  animation: dots-7ar3yq 0.8s infinite linear;
}

@keyframes dots-7ar3yq {
  20% {
    background-position:
      0% 0%,
      50% 50%,
      100% 50%;
  }

  40% {
    background-position:
      0% 100%,
      50% 0%,
      100% 50%;
  }

  60% {
    background-position:
      0% 50%,
      50% 100%,
      100% 0%;
  }

  80% {
    background-position:
      0% 50%,
      50% 50%,
      100% 100%;
  }
}
</style>
